import { z } from 'zod'
import { queryBoolean } from './zod-query'
import { idParam } from './id-validators'
import { TARGETING_LEAD_TYPE } from './targeting'
import type { ReisRouteProvider } from './reisuren'

export type LeadMapDTO = {
  id: string
  bedrijfsnaam: string
  type: 'Lead' | 'Klant'
  latitude: number
  longitude: number
  eigenaarId: string | null
  eigenaarNaam: string | null
  klantAlarmActief: boolean
  plaats: string | null
}

export type ClientMapDTO = {
  id: string
  naam: string
  primaireLocatieId: string
  latitude: number
  longitude: number
  plaats: string | null
}

export type TargetingRouteDTO = {
  km: number
  minuten: number
  provider: ReisRouteProvider
  polyline: string
}

export type PlaceSuggestionDTO = {
  placeId: string
  description: string
  mainText: string
  secondaryText: string
}

export type PlaceDetailsDTO = {
  straatnaam: string | null
  huisnummer: string | null
  huisnummerToevoeging: string | null
  postcode: string | null
  plaats: string | null
  provincie: string | null
  latitude: number | null
  longitude: number | null
}

export const leadMapDataQuerySchema = z.object({
  medewerkerId: idParam().optional(),
  straalKm: z.coerce.number().min(1).max(500).optional(),
  branche: z.string().optional(),
  zonderContract: queryBoolean().optional(),
  // Uitgebreide filters (BLOK 4) — gelijktrekken met listLeadsQuerySchema
  zoek: z.string().trim().min(1).optional(),
  plaats: z.string().trim().min(1).optional(),
  type: z.enum(TARGETING_LEAD_TYPE).optional(),
  eigenaarId: z.string().min(1).optional(),
  geenEigenaar: queryBoolean().optional(),
  minScore: z.coerce.number().int().min(0).max(5).optional(),
  minGrootte: z.coerce.number().int().min(0).optional(),
  klantAlarmActief: queryBoolean().optional(),
  metKvk: queryBoolean().optional(),
  metEmail: queryBoolean().optional(),
  provincie: z.string().trim().min(1).optional(),
  postcode: z.string().trim().min(1).optional(),
  label: z.string().trim().min(1).optional(),
  invoerdatumVan: z.string().datetime().optional(),
  invoerdatumTm: z.string().datetime().optional(),
  // Hard cap voor de kaart — bewust laag i.v.m. browser-performance bij
  // grote marker-aantallen. UI nudgt de gebruiker tot filteren. Bovengrens
  // 500 omdat clustering bij >500 markers de browser kan laten hangen.
  limit: z.coerce.number().int().min(1).max(500).default(200),
})

export const targetingRouteQuerySchema = z.object({
  fromLat: z.coerce.number().gte(-90).lte(90),
  fromLng: z.coerce.number().gte(-180).lte(180),
  toLat: z.coerce.number().gte(-90).lte(90),
  toLng: z.coerce.number().gte(-180).lte(180),
})

export const placesAutocompleteQuerySchema = z.object({
  q: z.string().min(2).max(200),
  country: z.string().length(2).default('NL'),
  sessionToken: z.string().min(1).max(200),
})

export const placesDetailsQuerySchema = z.object({
  placeId: z.string().min(1).max(500),
  sessionToken: z.string().min(1).max(200),
})

export const geocodeBulkResultSchema = z.object({
  jobId: z.string(),
  status: z.enum(['running', 'completed', 'failed']),
  totaal: z.number(),
  verwerkt: z.number(),
  geslaagd: z.number(),
  mislukt: z.number(),
  fouten: z.array(z.object({ leadId: z.string(), bericht: z.string() })),
})
export type GeocodeBulkResult = z.infer<typeof geocodeBulkResultSchema>
